import * as React from "react";
import {ReactNode, useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {Document, Page} from 'react-pdf'; // see https://github.com/wojtekmaj/react-pdf
import type {DocumentProps} from 'react-pdf';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSpinnerThird} from "@fortawesome/pro-regular-svg-icons";
import {faFilePdf} from "@fortawesome/pro-solid-svg-icons";
import ActionButton from "../presentational/button/ActionButton";

type PdfDocument = Parameters<NonNullable<DocumentProps['onLoadSuccess']>>[0];

type PdfViewerProps = {
    /** URL or binary data of the PDF; an empty value shows "noData" */
    file: DocumentProps['file']
    /** File name offered when the viewer's download button is used */
    filename: string
    /** Replaces the built-in download of the loaded data, e.g. for fetching a fresh, time-limited link */
    onDownload?: () => void
    isDownloading?: boolean
    /** Hides the download button, for pages that offer the download elsewhere */
    showDownload?: boolean
    noData?: ReactNode
    error?: ReactNode
    onLoadError?: (error: Error) => void
    className?: string
}

/**
 * Shows all pages of a PDF in a scrollable area, with the current page, the page count and a download button.
 */
const PdfViewer: React.FC<PdfViewerProps> = ({file, filename, onDownload, isDownloading, showDownload = true, noData, error, onLoadError, className = "h-[890px]"}) => {
    const intl = useIntl();
    const [pdfDocument, setPdfDocument] = useState<PdfDocument | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isPreparingDownload, setIsPreparingDownload] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        setPdfDocument(null);
        setCurrentPage(1);
        scrollContainerRef.current?.scrollTo({top: 0});
    }, [file]);

    const numberOfPages = pdfDocument?.numPages ?? 0;

    // The current page is the last one whose top edge has passed the middle of the visible area.
    // The container is positioned, so it is the offset parent of the page wrappers.
    const handleScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }
        const threshold = container.scrollTop + container.clientHeight / 2;
        let page = 1;
        pageRefs.current.slice(0, numberOfPages).forEach((element, index) => {
            if (element && element.offsetTop <= threshold) {
                page = index + 1;
            }
        });
        setCurrentPage(page);
    };

    const handleDownload = async () => {
        if (onDownload) {
            onDownload();
            return;
        }
        if (!pdfDocument) {
            return;
        }
        setIsPreparingDownload(true);
        try {
            const data = await pdfDocument.getData();
            const objectUrl = URL.createObjectURL(new Blob([new Uint8Array(data)], {type: 'application/pdf'}));
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = filename;
            link.click();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        } finally {
            setIsPreparingDownload(false);
        }
    };

    return (
        <div className="flex flex-col">
            {numberOfPages > 0 && (
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                    <span className="text-xs text-gray-500">
                        {intl.formatMessage({id: 'pdfViewer.pageOfPages'}, {current: currentPage, total: numberOfPages})}
                    </span>
                    {showDownload && (
                        <ActionButton
                            icon={faFilePdf}
                            label={intl.formatMessage({id: 'button.downloadPdf'})}
                            isSubmitting={isDownloading || isPreparingDownload}
                            onClick={handleDownload}
                        />
                    )}
                </div>
            )}
            <div ref={scrollContainerRef} onScroll={handleScroll} className={"relative overflow-y-auto " + className}>
                <Document
                    className="flex flex-col items-center gap-4 py-4"
                    file={file}
                    loading={(
                        <div className="text-center">
                            <FontAwesomeIcon icon={faSpinnerThird} spin={true}/>
                        </div>
                    )}
                    noData={noData}
                    error={error}
                    onLoadSuccess={setPdfDocument}
                    onLoadError={onLoadError}
                    onSourceError={onLoadError}
                >
                    {Array.from({length: numberOfPages}, (_, index) => (
                        <div key={index} ref={(element) => {
                            pageRefs.current[index] = element;
                        }} className="shadow-sm ring-1 ring-gray-200">
                            <Page pageNumber={index + 1} renderTextLayer={false} renderAnnotationLayer={false}/>
                        </div>
                    ))}
                </Document>
            </div>
        </div>
    );
}

export default PdfViewer;
